import React, { useContext, useEffect, useMemo, useState } from 'react';
import { useParams } from 'react-router-dom';
import { usePatientInfoQuery } from '../graphql';
import { CONSENT_MODAL_LABEL, defaultDateFormatWithAge, formatPhoneNumber } from '../utils';
import { useSelector } from 'react-redux';
import { AppRootState } from '../redux';
import './CaseDetailSidebarPatientInfo.scss';
import { HcpModal } from './HcpModal';
import ConsentModal from './consent-modal';
import { PatientProfileContext } from './patient-profile-context';
import { ConsentFlow } from './consent-modal/types';
import { Row, Col } from 'react-bootstrap';

interface CaseDetailSidebarPatientInfoParams {
  id: string;
}

export const CaseDetailSidebarPatientInfo = ({ id }: CaseDetailSidebarPatientInfoParams) => {
  const { selectedRequestInfo } = useContext(PatientProfileContext);
  const { id: urlId } = useParams<CaseDetailSidebarPatientInfoParams>();
  const finalId = id || urlId;
  const patientResult = usePatientInfoQuery({ variables: { patientId: finalId } });
  const [showConsentModal, setShowConsentModal] = useState(false);
  const patient = patientResult.data?.result;
  const patientPhoneNumber = patient?.phoneNumbers?.slice(0, 1).shift();
  const programId = useSelector((state: AppRootState) => state.auth.session?.user?.ProgramId) || '';
  const hasConsentFeature = useSelector(
    (state: AppRootState) =>
      (state.app.entities.features?.['show-patient-consent-invite-modal']?.data as any)?.enabled ?? false
  );

  const showPatientConsent = useMemo(
    () => hasConsentFeature && selectedRequestInfo,
    [selectedRequestInfo, hasConsentFeature]
  );

  const formatGender = (gender?: string | null, otherGender?: string | null) => {
    if ('F' === gender || 'FEMALE' === gender?.toUpperCase()) {
      return 'Female';
    } else if ('M' === gender || 'MALE' === gender?.toUpperCase()) {
      return 'Male';
    } else if ('O' === gender || 'OTHER' === gender?.toUpperCase()) {
      return otherGender?.toUpperCase();
    } else {
      return null;
    }
  };

  return (
    <div>
      <div className='h5 mx-3 mb-0' style={{ borderBottom: '2px solid #000' }}>
        <Row>
          <Col md={4}>
            <div className='text-secondary casedetailPatientInfo'>
              {patient?.lastName ? patient.lastName + ', ' : '-'}
              {patient?.firstName ?? '-'}
            </div>
          </Col>
          <Col md={2}>
            <div>
              <div className='patientDetail-label mt-3'>Date of Birth (Age):</div>
              <div className='patientDetail-textValue'>{defaultDateFormatWithAge(patient?.dob) ?? '-'}</div>
            </div>
          </Col>
          <Col>
            <div className=''>
              <div>
                <div className='patientDetail-label mt-3'>Gender:</div>
                <div className='patientDetail-textValue'>
                  {patient?.gender === 'O'
                    ? patient?.genderOther
                    : patient?.gender
                    ? formatGender(patient?.gender)
                    : '-'}
                </div>
              </div>
            </div>
          </Col>
          <Col>
            <div className=''>
              <div className='patientDetail-label mt-3'>Phone:</div>
              <div className='patientDetail-textValue'>
                {patientPhoneNumber?.phoneNumber ? formatPhoneNumber(patientPhoneNumber?.phoneNumber) : '-'}
              </div>
            </div>
          </Col>
          <Col>
            <div className=''>
              <div className='patientDetail-label mt-3'>City, State, Zip Code:</div>
              <div className='patientDetail-textValue'>
                <span>{patient?.address?.city ? patient?.address?.city + ', ' : ''}</span>
                <span>{patient?.address?.state ? patient?.address?.state + ', ' : ''}</span>
                <span>{patient?.address?.zip ?? ''}</span>
              </div>
            </div>
          </Col>
        </Row>
      </div>

      {hasConsentFeature && (
        <div className='d-flex my-3' style={{ marginLeft: '20px' }}>
          <div className='cmx__case-detail-sidebar-patient-info__grey-20-override  ml-3' style={{ fontWeight: '600' }}>
            PATIENT CONSENT
          </div>
          <div className='d-flex flex-row justify-content-between mr-3 mx-3'>
            <a
              className='cmx__case-detail-sidebar-patient-info__a-request-consent'
              onClick={() => setShowConsentModal(true)}>
              {CONSENT_MODAL_LABEL.REQUEST_CONSENT}
            </a>
          </div>
        </div>
      )}
      <HcpModal
        modalProps={{
          show: showConsentModal,
          onHide: () => {
            setShowConsentModal(false);
          },
          dialogClassName: 'consent-modal-dialog'
        }}
        body={
          <ConsentModal
            programId={programId}
            flow={ConsentFlow.REQUEST}
            patientInfo={patient}
            onClose={() => {
              setShowConsentModal(false);
            }}
          />
        }
      />
    </div>
  );
};
